import { useCallback, useMemo } from 'react';
import { ConfirmDialog } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { useRevokeAllocationMutation, useRejectAllocationMutation } from '../api/allocationsApi';
import { ALLOCATION_CONFIRM, buildAllocationConfirm } from '../constants/allocationConfirm';

/**
 * Props:
 *   module   the active module route segment
 *   confirm  { kind: ALLOCATION_CONFIRM.*, row } | null — the open question and the row it is about
 *   onClose  () => void — runs after either answer
 *   onEdit   (row) => void — the edit kind's follow-up; reject and revoke run here
 */
export const AllocationConfirmDialog = ({ module, confirm, onClose, onEdit }) => {
  const t = useTranslations();
  const [revokeAllocation] = useRevokeAllocationMutation();
  const [rejectAllocation] = useRejectAllocationMutation();

  const copy = useMemo(() => buildAllocationConfirm(t, confirm), [t, confirm]);

  const handleConfirm = useCallback(() => {
    const id = confirm?.row?.id;
    if (confirm?.kind === ALLOCATION_CONFIRM.revoke) revokeAllocation({ module, id });
    if (confirm?.kind === ALLOCATION_CONFIRM.reject) rejectAllocation({ module, id });
    if (confirm?.kind === ALLOCATION_CONFIRM.edit) onEdit?.(confirm.row);
    onClose();
  }, [confirm, module, revokeAllocation, rejectAllocation, onEdit, onClose]);

  return (
    <ConfirmDialog
      open={confirm != null}
      title={copy?.title}
      description={copy?.description}
      confirmLabel={copy?.confirmLabel}
      onConfirm={handleConfirm}
      onCancel={onClose}
    />
  );
};
